import React, { useState } from "react";
import { Minus, Plus, Truck, BadgeCheck, X } from "lucide-react";
import { money } from "../../lib/api";
import { useT } from "../../lib/i18n";
import {
  computeProductPrice, customRange, freeShippingState,
  firstOrderDiscountCents, FIRST_ORDER_PCT,
} from "../../lib/pricing";
import { NumberField } from "../NumberField";
import { ProductStack } from "../ProductStack";
import { KitBadges, kitBadges } from "../KitBadges";
import FirstOrderOffer from "./FirstOrderOffer";
import { track } from "../../lib/track";
import { toast } from "sonner";

const OFFER_SEEN_KEY = "mm_fo_offer_seen";

export default function KitStep({ draft, setDraft, settings }) {
  const { t } = useT();
  const kits = settings?.kits || [];
  const { enabled: customEnabled, min, max } = customRange(settings);
  const qty = draft.kit_qty;

  // live quantity used only for instant price display while typing
  const [liveQty, setLiveQty] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);

  // Show the first-order offer ONCE per session, only AFTER the first
  // meaningful interaction, and never when a discount is already applied.
  const maybeOpenOffer = () => {
    if (draft.discount) return;
    let seen = false;
    try { seen = !!localStorage.getItem(OFFER_SEEN_KEY); } catch { /* ignore */ }
    if (seen || offerOpen) return;
    try { localStorage.setItem(OFFER_SEEN_KEY, "1"); } catch { /* ignore */ }
    track("first_order_offer_shown");
    setOfferOpen(true);
  };

  const selectKit = (q) => { setLiveQty(null); setDraft((d) => ({ ...d, kit_qty: q })); maybeOpenOffer(); };
  const setCustomQty = (q) => {
    let n = Math.round(Number(q) || 0);
    if (n < min) n = min;
    if (max != null && n > max) n = max;
    setLiveQty(null);
    setDraft((d) => ({ ...d, kit_qty: n }));
    maybeOpenOffer();
  };

  const applyDiscount = (discount) => {
    setDraft((d) => ({ ...d, discount }));
    setOfferOpen(false);
    toast.success(t("fo_applied_toast", { pct: discount.pct || FIRST_ORDER_PCT }));
  };
  const removeDiscount = () => setDraft((d) => ({ ...d, discount: null }));

  const stepperValue = qty != null ? qty : min;
  const displayQty = liveQty != null && liveQty !== "" ? Number(liveQty) : qty;
  const pricing = displayQty ? computeProductPrice(settings, displayQty) : null;
  const isCustomActive = qty != null && !kits.some((k) => k.qty === qty);

  const subtotal = pricing ? pricing.product_price_cents : 0;
  const discountActive = !!(draft.discount && draft.discount.type === "first_order");
  const discountCents = firstOrderDiscountCents(subtotal, discountActive);

  // Free-shipping message follows the ACTIVE rule. Value rule uses the
  // pre-discount magnet subtotal; quantity rule uses the magnet quantity.
  const effQty = Number(displayQty || 0);
  const fs = freeShippingState(settings, effQty, subtotal);
  let freeShip = null;
  if (fs) {
    if (fs.unlocked) {
      freeShip = { unlocked: true, text: t("free_unlocked") };
    } else if (fs.rule === "magnet_qty") {
      freeShip = fs.hasValue
        ? { unlocked: false, text: t("free_away_qty", { n: fs.remainingQty }) }
        : { unlocked: false, text: t("free_hint_qty", { n: fs.threshold }) };
    } else {
      freeShip = fs.hasValue
        ? { unlocked: false, text: t("free_away_value", { amt: money(fs.remainingCents) }) }
        : { unlocked: false, text: t("free_hint_value", { amt: money(fs.threshold) }) };
    }
  }

  return (
    <div>
      <div className="flex justify-center py-1" aria-hidden="true">
        <ProductStack size={52} />
      </div>

      <div className="mt-3 grid grid-cols-2 items-stretch gap-3 sm:grid-cols-3" data-testid="kit-selection-grid">
        {kits.map((k) => {
          const selected = qty === k.qty;
          const badges = kitBadges(k, settings, t);
          return (
            <button
              key={k.qty}
              type="button"
              data-testid={`wizard-kit-${k.qty}`}
              onClick={() => selectKit(k.qty)}
              className={`relative flex h-full flex-col rounded-[1.1rem] border bg-card p-4 text-left shadow-[var(--shadow-card)] transition-[box-shadow,border-color] duration-200 ${
                selected ? "ring-2" : "hover:bg-[hsl(var(--muted))]"
              }`}
              style={selected ? { borderColor: "hsl(var(--brand-rose))", "--tw-ring-color": "hsl(var(--brand-rose))" } : {}}
            >
              <div className="min-h-[22px]"><KitBadges badges={badges} /></div>
              <div className="mt-1 flex items-baseline gap-1.5">
                <span className="font-display text-2xl font-semibold leading-none" style={{ color: "hsl(var(--brand-brown))" }}>{k.qty}</span>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">{t("magnets")}</span>
              </div>
              <span className="mt-2 font-display text-lg font-semibold tabular-nums" style={{ color: "hsl(var(--brand-rose))" }}>{money(k.price_cents)}</span>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{money(Math.round(k.price_cents / k.qty))} {t("per_magnet")}</span>
            </button>
          );
        })}

        {customEnabled && (
          <div
            data-testid="custom-quantity-section"
            className={`col-span-2 flex flex-col rounded-[1.1rem] border border-dashed bg-card p-4 sm:col-span-3 ${isCustomActive ? "ring-2" : ""}`}
            style={isCustomActive ? { borderColor: "hsl(var(--brand-rose))", "--tw-ring-color": "hsl(var(--brand-rose))" } : {}}
          >
            <div className="text-sm font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("custom_card_title")}</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">{max != null ? `${min}\u2013${max} ${t("magnets")}` : `${t("magnets")} ${min}+`}</p>
            <div className="mt-3 flex items-center gap-3">
              <button type="button" aria-label="Decrease quantity" data-testid="custom-qty-minus" onClick={() => setCustomQty(stepperValue - 1)} disabled={stepperValue <= min} className="grid h-11 w-11 place-items-center rounded-xl border bg-white text-[hsl(var(--brand-brown))] disabled:opacity-40"><Minus className="h-5 w-5" /></button>
              <NumberField value={stepperValue} min={min} onLiveChange={(v) => setLiveQty(v)} onCommit={(v) => setCustomQty(v)} className="h-11 w-20 rounded-xl text-center text-lg font-semibold tabular-nums" data-testid="custom-qty-input" />
              <button type="button" aria-label="Increase quantity" data-testid="custom-qty-plus" onClick={() => setCustomQty(stepperValue + 1)} disabled={max != null && stepperValue >= max} className="grid h-11 w-11 place-items-center rounded-xl border bg-white text-[hsl(var(--brand-brown))] disabled:opacity-40"><Plus className="h-5 w-5" /></button>
              <div className="ml-auto text-right">
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t("your_price")}</div>
                <div className="font-display text-xl font-semibold tabular-nums" style={{ color: "hsl(var(--brand-rose))" }} data-testid="custom-qty-price">{pricing ? money(pricing.product_price_cents) : "\u2014"}</div>
                {pricing && displayQty > 0 && (
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]" data-testid="custom-qty-per-magnet">{money(Math.round(pricing.product_price_cents / displayQty))} {t("per_magnet")}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* First-order discount breakdown (magnet subtotal only) */}
      {discountActive && subtotal > 0 && (
        <div className="mt-4 rounded-xl border p-3" style={{ borderColor: "hsl(var(--success))", background: "rgba(31,122,90,0.08)" }} data-testid="first-order-discount-applied">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "hsl(var(--success))" }}>
              <BadgeCheck className="h-4 w-4 shrink-0" /> {t("fo_savings", { pct: FIRST_ORDER_PCT, amt: money(discountCents) })}
            </span>
            <button type="button" onClick={removeDiscount} className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] underline underline-offset-2 hover:text-[hsl(var(--brand-brown))]" data-testid="first-order-remove">
              <X className="h-3 w-3" /> {t("fo_remove")}
            </button>
          </div>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">{t("fo_original")}</dt><dd className="tabular-nums text-[hsl(var(--muted-foreground))] line-through">{money(subtotal)}</dd></div>
            <div className="flex justify-between"><dt className="text-[hsl(var(--muted-foreground))]">{t("first_order_discount_label", { pct: FIRST_ORDER_PCT })}</dt><dd className="tabular-nums" style={{ color: "hsl(var(--success))" }}>−{money(discountCents)}</dd></div>
            <div className="flex justify-between font-semibold"><dt style={{ color: "hsl(var(--brand-brown))" }}>{t("fo_discounted")}</dt><dd className="tabular-nums" style={{ color: "hsl(var(--brand-brown))" }} data-testid="first-order-discounted-total">{money(subtotal - discountCents)}</dd></div>
          </dl>
        </div>
      )}

      {freeShip && (
        <div
          data-testid="free-shipping-message"
          className="mt-4 flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-center text-sm font-medium"
          style={freeShip.unlocked
            ? { background: "rgba(31,122,90,0.12)", color: "hsl(var(--success))" }
            : { background: "hsl(var(--muted))", color: "hsl(var(--brand-brown))" }}
        >
          <Truck className="h-4 w-4 shrink-0" />
          <span>{freeShip.text}</span>
        </div>
      )}

      <FirstOrderOffer
        open={offerOpen}
        kitQty={qty}
        onClose={() => setOfferOpen(false)}
        onApplied={applyDiscount}
      />
    </div>
  );
}
